/**
 * FirmEditDialog — the "Bearbeiten" action-menu edit surface for a Firma (D1-14).
 *
 * Wired from the Kunde detail header's top-right action menu (was a stub). Fetches the current
 * Firmenstammdaten, lets the broker edit them in the shared FirmStammdatenForm, and PUTs the
 * changes to /firms/:id. On success it hands the refreshed detail back so the header + card
 * re-render, and reports the edited-field-survives-enrichment guarantee is now server-side.
 *
 * [Source: apps/web/src/features/entity-detail/components/FirmStammdatenForm.tsx]
 * [Source: apps/web/src/features/administration/components.tsx] (Dialog, Button)
 */

import { useEffect, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Dialog, Button } from '../../administration/components';
import { apiClient } from '../../../lib/api-client';
import type { FirmDetailWire } from '../../../lib/api-client';
import {
  FirmStammdatenForm,
  draftFromWire,
  draftToPatch,
  draftUnchanged,
  referenceCurrencyInvalid,
  annualRevenueInvalid,
  type FirmDraft,
} from './FirmStammdatenForm';

export function FirmEditDialog({
  firmaId,
  onClose,
  onSaved,
}: {
  firmaId: string;
  onClose: () => void;
  /** Called with the refreshed detail after a successful save. */
  onSaved: (updated: FirmDetailWire) => void;
}) {
  const [original, setOriginal] = useState<FirmDraft | null>(null);
  const [draft, setDraft] = useState<FirmDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.firms
      .get(firmaId)
      .then((res) => {
        if (cancelled) return;
        if (res.restricted === true) {
          setError('Dieser Kunde ist gesperrt und kann nicht bearbeitet werden.');
          return;
        }
        const d = draftFromWire(res);
        setOriginal(d);
        setDraft(d);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Die Stammdaten konnten nicht geladen werden.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [firmaId]);

  function onField(field: keyof FirmDraft, value: string) {
    setDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
  }

  async function save() {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await apiClient.firms.update(firmaId, draftToPatch(draft));
      onSaved(updated);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Die Stammdaten konnten nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  }

  const nameEmpty = draft !== null && draft.name.trim() === '';
  // P2-8: reference_currency is NOT NULL at the DB — mirrors the nameEmpty gate below.
  const currencyInvalid = draft !== null && referenceCurrencyInvalid(draft);
  // P2-8 (Fix-B/W-F2): annual_revenue is numeric(14,2) — a non-numeric value must block Save
  // rather than round-trip into a 400/500 (mirrors the server DTO's NUMERIC_14_2_RE).
  const revenueInvalid = draft !== null && annualRevenueInvalid(draft);
  const noChanges = original !== null && draft !== null && draftUnchanged(original, draft);

  return (
    <Dialog
      open
      onClose={() => {
        if (!saving) onClose();
      }}
      title="Firmenstammdaten bearbeiten"
      description="Von Hand geänderte Felder werden beim nächsten Handelsregister-Abgleich nicht überschrieben."
      wide
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            onClick={() => void save()}
            disabled={saving || loading || draft === null || nameEmpty || currencyInvalid || revenueInvalid || noChanges}
          >
            {saving ? 'Wird gespeichert …' : 'Speichern'}
          </Button>
        </>
      }
    >
      {loading && (
        <p className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Wird geladen …
        </p>
      )}

      {error !== null && (
        <p className="mb-3 flex items-start gap-2 text-sm text-destructive">
          <AlertCircle className="size-4 shrink-0" />
          {error}
        </p>
      )}

      {!loading && draft !== null && (
        <FirmStammdatenForm draft={draft} onField={onField} disabled={saving} />
      )}

      {nameEmpty && (
        <p className="mt-2 text-xs text-destructive">Der Firmenname darf nicht leer sein.</p>
      )}
      {currencyInvalid && (
        <p className="mt-2 text-xs text-destructive">
          Die Referenzwährung muss ein gültiger 3-stelliger Code sein (z. B. CHF).
        </p>
      )}
    </Dialog>
  );
}
